/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, ChangeEvent, useContext, useEffect } from 'react';
import {
  Modal, Typography, Button, Grid, DialogTitle, Dialog, DialogContent,
  IconButton, Box, Tabs, Tab, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';
import { exportFile } from '../../../utils';
import { X } from '../../../assets';
import { Loader } from '../../../components/ui/Loader';
import CustomPagination from '../../../components/forms/Pagination';

type InvoiceImportValidationError = {
  rowNumber?: number;
  invoiceNumber?: string;
  orderNumber?: string;
  invoiceDate?: string;
  deliveryDate?: string;
  itemNumber?: string;
  quantity?: number;
  [key: string]: any;
};

type InvoiceValidationErrors = Record<string, InvoiceImportValidationError[]>;

const VALIDATION_TABS = ['ParseError', 'OrderNumberNotFound'] as const;
type ValidationTab = typeof VALIDATION_TABS[number];

const TAB_DESCRIPTION_KEYS: Record<ValidationTab, string> = {
  ParseError: 'ParseErrorDesc',
  OrderNumberNotFound: 'OrderNumberNotFoundDesc',
};

const ERRORS_PAGE_SIZE = 5;

interface InvoiceBulkManageProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: (message: string) => void;
}

const InvoiceBulkManage: React.FC<InvoiceBulkManageProps> = ({ open, handleClose, onSuccess }) => {
  const { t } = useTranslation('invoices');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<InvoiceValidationErrors | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tabPages, setTabPages] = useState<Record<number, number>>({ 0: 0, 1: 0 });
  const { exportInvoicesToExcel, exportInvoicesToCsv, importInvoicesFromFile } = useNswagClient();
  const { selectedStore } = useContext(UserContext);

  useEffect(() => {
    if (!open) {
      setErrorMessage(null);
      setSuccessMessage(null);
      setSelectedFile(null);
      setValidationErrors(null);
      setActiveTab(0);
      setTabPages({ 0: 0, 1: 0 });
    }
  }, [open]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(null);
    setValidationErrors(null);
    const file = event.target.files?.[0] ?? null;
    if (file) {
      setSelectedFile(file);
      setSuccessMessage(t('fileSelected', { name: file.name }));
    }
    event.target.value = '';
  };

  const handleDownloadExcel = () => {
    if (!selectedStore?.storeNumber) return;
    setIsExportingExcel(true);
    exportInvoicesToExcel(selectedStore.storeNumber)
      .then((response) => {
        if (response) {
          exportFile(
            response.data,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            `Invoices-${selectedStore.storeNumber}.xlsx`,
          );
        }
      })
      .catch(() => setErrorMessage(t('error')))
      .finally(() => setIsExportingExcel(false));
  };

  const handleDownloadCsv = () => {
    if (!selectedStore?.storeNumber) return;
    setIsExportingCsv(true);
    exportInvoicesToCsv(selectedStore.storeNumber)
      .then((response) => {
        if (response) {
          exportFile(
            response.data,
            'text/csv',
            `Invoices-${selectedStore.storeNumber}.csv`,
          );
        }
      })
      .catch(() => setErrorMessage(t('error')))
      .finally(() => setIsExportingCsv(false));
  };

  const handleSave = async () => {
    if (!selectedFile || !selectedStore?.storeNumber) return;
    setIsSaving(true);
    setErrorMessage(null);
    setValidationErrors(null);
    try {
      const result = await importInvoicesFromFile(selectedStore.storeNumber, selectedFile);
      const hasErrors = result && Object.values(result).some((list) => list.length > 0);
      if (hasErrors) {
        setValidationErrors(result as InvoiceValidationErrors);
        setActiveTab(0);
        setTabPages({ 0: 0, 1: 0 });
      } else {
        setSelectedFile(null);
        handleClose();
        onSuccess(t('uploadSuccess'));
      }
      setSuccessMessage(null);
    } catch {
      setErrorMessage(t('importError'));
      setSuccessMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  const getTabItems = (tabIndex: number): InvoiceImportValidationError[] => {
    if (!validationErrors) return [];
    return validationErrors[VALIDATION_TABS[tabIndex]] ?? [];
  };

  const getPagedItems = (tabIndex: number): InvoiceImportValidationError[] => {
    const items = getTabItems(tabIndex);
    const page = tabPages[tabIndex] ?? 0;
    return items.slice(page * ERRORS_PAGE_SIZE, (page + 1) * ERRORS_PAGE_SIZE);
  };

  const getTotalPages = (tabIndex: number): number => {
    return Math.ceil(getTabItems(tabIndex).length / ERRORS_PAGE_SIZE);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="invoice-bulk-modal-title"
    >
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            id="invoice-bulk-modal-title"
            variant="textXL"
            sx={{ display: 'flex', flex: 1, justifyContent: 'left', alignItems: 'center' }}
          >
            {t('bulkManageTitle')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ p: 0 }}
          >
            <X />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              textAlign: 'left',
              mb: 3,
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            {t('bulkManageDescription')}
          </Typography>

          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'left', columnGap: '10px' }}
          >
            <Button
              size='lg'
              variant='secondary'
              onClick={handleDownloadExcel}
              disabled={isExportingExcel}
            >
              {isExportingExcel ? t('exporting') : t('downloadExcel')}
            </Button>
            <Button
              size='lg'
              variant='secondary'
              onClick={handleDownloadCsv}
              disabled={isExportingCsv}
            >
              {isExportingCsv ? t('exporting') : t('downloadCsv')}
            </Button>
            <Button
              size='lg'
              onClick={() => document.getElementById('invoiceFileInput')?.click()}
            >
              {t('upload')}
            </Button>
          </Grid>

          <input
            aria-hidden
            type="file"
            id="invoiceFileInput"
            accept=".xlsx,.csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {errorMessage && (
            <Typography
              color="error"
              sx={{ mt: 2, fontSize: 18 }}
            >
              {errorMessage}
            </Typography>
          )}

          {isSaving && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 18, color: 'text.secondary' }}>
                {t('validating')}
              </Typography>
            </Box>
          )}

          {successMessage && !isSaving && !validationErrors && (
            <Box sx={{ mt: 5, ml: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 23 }} />
              <Typography sx={{ fontSize: 16, color: 'success.main' }}>
                {successMessage}
              </Typography>
            </Box>
          )}

          {/* Validation errors panel */}
          {validationErrors && (
            <Box sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography sx={{ px: 2, pt: 2, fontWeight: 600, fontSize: '0.95rem' }}>
                {t('validationTitle')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{ flex: 1 }}
                >
                  {VALIDATION_TABS.map((key) => (
                    <Tab
                      key={key}
                      label={`${t(key as any)} (${(validationErrors[key] ?? []).length})`}
                      sx={{ fontSize: '0.78rem', textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5, fontFamily: 'inherit' }}>
                      {VALIDATION_TABS.map((key, i) => (
                        <Box key={key}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: 'inherit' }}>
                            {t(key as any)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'inherit' }}>
                            {t(TAB_DESCRIPTION_KEYS[key] as any)}
                          </Typography>
                          {i < VALIDATION_TABS.length - 1 && (
                            <Box sx={{ height: '12px' }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  }
                  arrow
                  placement="left"
                >
                  <IconButton
                    size="medium"
                    sx={{ ml: 1, color: 'text.secondary' }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {VALIDATION_TABS.map((key, tabIndex) => (
                <Box
                  key={key}
                  hidden={activeTab !== tabIndex}
                  sx={{ px: 2, pb: 2 }}
                >
                  {activeTab === tabIndex && (
                    <>
                      {getTabItems(tabIndex).length === 0 ? (
                        <Typography sx={{ py: 2, color: 'text.secondary', fontSize: '0.85rem' }}>
                          {t('noValidationErrors')}
                        </Typography>
                      ) : (
                        <>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('colRowNumber')}</TableCell>
                                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('colInvoiceNumber')}</TableCell>
                                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('colOrderNumber')}</TableCell>
                                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('colItemNumber')}</TableCell>
                                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('colQuantity')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {getPagedItems(tabIndex).map((item, i) => (
                                  <TableRow key={i}>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{item.rowNumber ?? '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{item.invoiceNumber ?? '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{item.orderNumber ?? '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{item.itemNumber ?? '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{item.quantity ?? '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {getTotalPages(tabIndex) > 1 && (
                            <CustomPagination
                              page={tabPages[tabIndex] ?? 0}
                              setPage={(p) => setTabPages(prev => ({ ...prev, [tabIndex]: p }))}
                              maxPages={getTotalPages(tabIndex)}
                              breakpointForChangeDisplay={120000}
                              useNoDataMessage={false}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'right',
              columnGap: { xs: '8px', sm: '10px' },
              mt: 3,
            }}
          >
            <Button
              size="lg"
              variant="secondary"
              onClick={handleClose}
              sx={{ marginTop: { xs: '8px', sm: '0' } }}
            >
              {t('cancel')}
            </Button>
            <Button
              size="lg"
              onClick={handleSave}
              disabled={!selectedFile || !!errorMessage || isSaving || !!validationErrors}
              sx={{ marginTop: { xs: '8px', sm: '0' } }}
            >
              {isSaving ? <Loader /> : t('save')}
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </Modal>
  );
};

export default InvoiceBulkManage;